/**
 * Incremental KGRB bundle parser (pure — unit tested): feed it network chunks of any size and it
 * emits each page Blob as soon as all of that page's bytes have arrived, so pages appear while the
 * rest of the bundle is still downloading. Consumed chunks are dropped to keep memory flat.
 */
import { BUNDLE_PREFIX, parseHeader, readPrefix, type BundleHeader } from '../../shared/bundle';

const PAGE_TYPES = new Set(['image/webp', 'image/jpeg', 'image/png']);
const MAX_PAGES = 1000;

export interface BundleStreamCallbacks {
  onHeader?: (header: BundleHeader) => void;
  onPage: (index: number, blob: Blob) => void;
}

export class BundleError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BundleError';
  }
}

function validateHeader(h: BundleHeader): void {
  if (h.pages.length === 0 || h.pages.length > MAX_PAGES) throw new BundleError('bad page count');
  for (const p of h.pages) {
    if (
      !Number.isSafeInteger(p.offset) ||
      !Number.isSafeInteger(p.len) ||
      p.offset < 0 ||
      p.len <= 0 ||
      !PAGE_TYPES.has(p.type)
    ) {
      throw new BundleError('bad page entry');
    }
  }
}

export class BundleStreamParser {
  private chunks: Uint8Array[] = [];
  /** Absolute byte offset of chunks[0]. */
  private chunksStart = 0;
  private received = 0;
  private headerLen = -1;
  private header: BundleHeader | null = null;
  private base = 0;
  /** Page indices sorted by end offset (emission order). */
  private order: number[] = [];
  private next = 0;

  constructor(private readonly cb: BundleStreamCallbacks) {}

  get done(): boolean {
    return this.header !== null && this.next >= this.order.length;
  }

  get bundleHeader(): BundleHeader | null {
    return this.header;
  }

  get bytesReceived(): number {
    return this.received;
  }

  push(chunk: Uint8Array): void {
    if (chunk.byteLength === 0 || this.done) return;
    this.chunks.push(chunk);
    this.received += chunk.byteLength;
    this.process();
  }

  /** Call when the stream ended; throws when the bundle was truncated. */
  finish(): void {
    if (!this.done) throw new BundleError('bundle truncated');
  }

  private process(): void {
    if (this.headerLen < 0) {
      if (this.received < BUNDLE_PREFIX) return;
      try {
        this.headerLen = readPrefix(this.read(0, BUNDLE_PREFIX));
      } catch (e) {
        throw new BundleError((e as Error).message);
      }
    }
    if (!this.header) {
      const end = BUNDLE_PREFIX + this.headerLen;
      if (this.received < end) return;
      let h: BundleHeader;
      try {
        h = parseHeader(this.read(BUNDLE_PREFIX, end));
      } catch (e) {
        throw new BundleError((e as Error).message);
      }
      validateHeader(h);
      this.header = h;
      this.base = end;
      this.order = h.pages.map((_, i) => i).sort((a, b) => {
        const pa = h.pages[a]!;
        const pb = h.pages[b]!;
        return pa.offset + pa.len - (pb.offset + pb.len) || a - b;
      });
      this.cb.onHeader?.(h);
    }
    const h = this.header;
    while (this.next < this.order.length) {
      const idx = this.order[this.next]!;
      const p = h.pages[idx]!;
      const start = this.base + p.offset;
      const end = start + p.len;
      if (this.received < end) break;
      const blob = new Blob(this.slices(start, end), { type: p.type });
      this.next++;
      this.cb.onPage(idx, blob);
    }
    this.release();
  }

  /** Contiguous copy of absolute bytes [a, b) (only used for the small prefix/header). */
  private read(a: number, b: number): Uint8Array {
    const out = new Uint8Array(b - a);
    let pos = 0;
    for (const part of this.slices(a, b)) {
      out.set(part, pos);
      pos += part.byteLength;
    }
    return out;
  }

  /** Views over the buffered chunks covering absolute bytes [a, b). */
  private slices(a: number, b: number): Uint8Array<ArrayBuffer>[] {
    const out: Uint8Array<ArrayBuffer>[] = [];
    let off = this.chunksStart;
    for (const c of this.chunks) {
      const cEnd = off + c.byteLength;
      if (cEnd > a && off < b) {
        const s = Math.max(a, off) - off;
        const e = Math.min(b, cEnd) - off;
        out.push(c.subarray(s, e) as Uint8Array<ArrayBuffer>);
      }
      off = cEnd;
      if (off >= b) break;
    }
    return out;
  }

  /** Drop chunks that lie entirely before every byte still needed. */
  private release(): void {
    if (!this.header) return;
    let keepFrom = Infinity;
    for (let i = this.next; i < this.order.length; i++) {
      const p = this.header.pages[this.order[i]!]!;
      keepFrom = Math.min(keepFrom, this.base + p.offset);
    }
    while (this.chunks.length > 0) {
      const first = this.chunks[0]!;
      if (this.chunksStart + first.byteLength > keepFrom) break;
      this.chunksStart += first.byteLength;
      this.chunks.shift();
    }
  }
}

/**
 * Reads a bundle from a response body stream, emitting pages as they complete.
 * Resolves with the header once every page has been emitted.
 */
export async function streamBundle(
  body: ReadableStream<Uint8Array>,
  cb: BundleStreamCallbacks,
  signal?: AbortSignal,
): Promise<BundleHeader> {
  const parser = new BundleStreamParser(cb);
  const reader = body.getReader();
  const onAbort = () => void reader.cancel().catch(() => undefined);
  signal?.addEventListener('abort', onAbort, { once: true });
  try {
    for (;;) {
      if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
      const { done, value } = await reader.read();
      if (done) break;
      if (value) parser.push(value);
      if (parser.done) {
        void reader.cancel().catch(() => undefined);
        break;
      }
    }
    if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
    parser.finish();
    return parser.bundleHeader!;
  } catch (e) {
    // Corrupt bundle (or a failing callback): stop the download instead of letting up to the whole
    // body keep arriving in the background.
    void reader.cancel().catch(() => undefined);
    throw e;
  } finally {
    signal?.removeEventListener('abort', onAbort);
    try {
      reader.releaseLock();
    } catch {
      /* already released */
    }
  }
}

/** Same as streamBundle() for an already complete Blob (e.g. from a cache). */
export async function readBundleBlob(blob: Blob, cb: BundleStreamCallbacks): Promise<BundleHeader> {
  const parser = new BundleStreamParser(cb);
  const prefix = new Uint8Array(await blob.slice(0, BUNDLE_PREFIX).arrayBuffer());
  parser.push(prefix);
  const hlen = readPrefix(prefix);
  parser.push(new Uint8Array(await blob.slice(BUNDLE_PREFIX, BUNDLE_PREFIX + hlen).arrayBuffer()));
  const header = parser.bundleHeader;
  if (!header) throw new BundleError('bad header');
  // Pages are zero-copy slices of the blob; the parser only validated the header.
  const base = BUNDLE_PREFIX + hlen;
  const end = Math.max(...header.pages.map((p) => p.offset + p.len));
  if (blob.size < base + end) throw new BundleError('bundle truncated');
  header.pages.forEach((p, i) => cb.onPage(i, blob.slice(base + p.offset, base + p.offset + p.len, p.type)));
  return header;
}
